import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChangedListener, logOut } from '../firebase';
import { API_BASE_URL } from '../services/apiBase';

// ── Access control ───────────────────────────────────────────
// The email allowlist lives on the BACKEND: verify_token checks
// ALLOWED_EMAILS in auth.py on every API request. After sign-in we
// probe /profile once — a 403 means the account is not allowlisted,
// so we sign out and show the denied message.

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  accessDenied: boolean;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  accessDenied: false,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener(async (user: User | null) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          const res = await fetch(`${API_BASE_URL}/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.status === 403) {
            // Backend allowlist rejected this account — sign them out
            await logOut();
            setCurrentUser(null);
            setAccessDenied(true);
          } else {
            // 200 (profile exists), 404 (new allowed user) and transient
            // errors all proceed — every API call re-enforces the allowlist
            setCurrentUser(user);
            setAccessDenied(false);
          }
        } catch {
          // Backend unreachable — let the app load; pages surface their own errors
          setCurrentUser(user);
          setAccessDenied(false);
        }
      } else {
        setCurrentUser(null);
        // Keep accessDenied if it was just set (so the message shows)
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    accessDenied,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
